import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { formatResponse } from './helpers/formatResponse'
import type { ProposedPanelV1 } from '@/types/DashboardV1.types'

/**
 * DashboardProposedPanelsAPI — deterministic proposed-panel generation.
 *
 * Backed by `POST /v1/dashboards/:id/proposed-panels`
 * (Slice 1.2 of the Step 5 Deterministic Panels plan,
 * `plans/2026-04-29-step-5-deterministic-panels-and-chat-context.md` §Slice 1.2).
 *
 * The success body is the **raw** response shape `{ proposedPanels: [...] }` —
 * NOT wrapped in the legacy `{ success, data, messages }` `TCSAPIResponse`
 * envelope. Per CEO 2026-04-28 directive, TCS is moving away from the
 * envelope/wrapper for new endpoints. We therefore bypass `formatResponse`
 * (which unwraps the envelope) on the success path and consume `resp.data`
 * directly. The error path still uses `formatResponse` because Nest error
 * bodies retain the `{ message }` / `{ messages }` shape.
 *
 * Request body: `{ idempotencyKey?: string }`.
 * The endpoint reads `domainIds` and filter values from `flow_context`
 * server-side — no client-supplied filter payload.
 *
 * Error 409 signals FSM state too early (Conflict — Confirm out of order).
 * 5xx signals upstream / panel-library failure.
 */

export interface ProposedPanelsResponse {
  proposedPanels: ProposedPanelV1[]
}

export const DashboardProposedPanelsAPI = {
  proposePanels: async (
    token: string,
    args: {
      /** UUID of the dashboard whose `flow_context` holds the confirmed filters. */
      dashboardId: string
      /**
       * Optional idempotency key — generated per Confirm-click invocation by
       * the service thunk. Prevents duplicate persists on rapid double-click.
       */
      idempotencyKey?: string
    }
  ): Promise<{
    data: ProposedPanelsResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
  }> => {
    try {
      const body: { idempotencyKey?: string } = {}
      if (args.idempotencyKey) {
        body.idempotencyKey = args.idempotencyKey
      }
      const resp: AxiosResponse<ProposedPanelsResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${encodeURIComponent(args.dashboardId)}/proposed-panels`,
        headers: { Authorization: `Bearer ${token}` },
        data: body,
      })
      // Raw shape — no envelope unwrap. resp.data IS the ProposedPanelsResponse.
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{ error?: string; reason?: string }>
      const status = axiosErr.response?.status
      const body = axiosErr.response?.data
      const errorCode = body?.error ?? body?.reason
      const formatted = formatResponse<ProposedPanelsResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
      }
    }
  },
}
